import { ImageBox } from '../components/BoxedImage';
import { TagComplianceOrphanPriceTag, TagCompliancePreview, TagComplianceShelf } from '../types';

export const TAG_MISSING_LAYER = 'tag_missing';
export const TAG_EXTRA_LAYER = 'tag_extra';
export const TAG_PRICE_CHECK_LAYER = 'tag_price_check';
export const TAG_SKIPPED_LAYER = 'tag_skipped';
export const TAG_ORPHAN_LAYER = 'orphan_tag';

const ORPHAN_TAG_COLOR = '#a855f7';

/**
 * The shelf outcome a layer represents. Mirrors the backend's verdict: a shelf
 * emits a count subtask, or is skipped with a reason, or defers to the price
 * check — never more than one.
 */
type ShelfOutcome =
  | typeof TAG_MISSING_LAYER
  | typeof TAG_EXTRA_LAYER
  | typeof TAG_PRICE_CHECK_LAYER
  | typeof TAG_SKIPPED_LAYER;

/**
 * Which layer a shelf belongs to. Skip is checked before the subtask type
 * because a skipped shelf carries no subtask, and before priceCheck because the
 * backend returns before the count comparison when it skips.
 */
const shelfOutcome = (shelf: TagComplianceShelf): ShelfOutcome => {
  if (shelf.skipReason) {
    return TAG_SKIPPED_LAYER;
  }

  if (shelf.subtaskType === 'missing_tags') {
    return TAG_MISSING_LAYER;
  }

  if (shelf.subtaskType === 'extra_tags') {
    return TAG_EXTRA_LAYER;
  }

  return TAG_PRICE_CHECK_LAYER;
};

/** What a clicked tag-compliance overlay box carries, for the details dialog. */
export type TagComplianceOverlayItem =
  | { kind: 'shelf'; shelf: TagComplianceShelf }
  | { kind: 'orphan'; shelf: TagComplianceShelf; tag: TagComplianceOrphanPriceTag };

export type TagComplianceImageBox = ImageBox & { tagComplianceItem: TagComplianceOverlayItem };

/**
 * The tag-compliance layers, shown alongside the compliance action layers.
 *
 * Letters rather than digits: the audit page shows the compliance and detection
 * layer columns side by side, and the detection layers own the digits there.
 * The obvious initials are taken too - the action layers claim c/m/o/r/u - so
 * these are the next-best mnemonics.
 */
export const TAG_COMPLIANCE_LAYER_CONFIG = [
  {
    key: TAG_MISSING_LAYER,
    label: 'Tags: missing',
    color: 'bg-red-500',
    bboxColor: '#ef4444',
    shortcut: 'g',
  },
  {
    key: TAG_EXTRA_LAYER,
    label: 'Tags: extra',
    color: 'bg-amber-500',
    bboxColor: '#f59e0b',
    shortcut: 'x',
  },
  {
    key: TAG_PRICE_CHECK_LAYER,
    // Not "no issues": the count balanced, so the shelf defers to the price
    // check, which can still fail it on the printed price.
    label: 'Tags: count OK',
    color: 'bg-blue-500',
    bboxColor: '#3b82f6',
    shortcut: 'k',
  },
  {
    key: TAG_SKIPPED_LAYER,
    label: 'Tags: skipped',
    color: 'bg-gray-400',
    bboxColor: '#9ca3af',
    shortcut: 's',
  },
  {
    key: TAG_ORPHAN_LAYER,
    label: 'Tags: orphaned',
    color: 'bg-fuchsia-500',
    bboxColor: ORPHAN_TAG_COLOR,
    shortcut: 'p',
  },
];

const BBOX_COLOR_BY_LAYER = new Map(
  TAG_COMPLIANCE_LAYER_CONFIG.map((layer) => [layer.key, layer.bboxColor])
);

const isModEnabled = (shelf: TagComplianceShelf, enabledMods?: Set<string>): boolean =>
  !enabledMods || enabledMods.size === 0 || enabledMods.has(shelf.tagName);

/**
 * Clickable overlay boxes for the tag-compliance preview: one box per shelf (the
 * union of its template boxes), colored by outcome, plus each orphan price tag.
 * Each box carries its source row on `tagComplianceItem` so a click can show the
 * details.
 */
export function buildTagComplianceOverlay(
  preview: TagCompliancePreview | null | undefined,
  enabledLayers: Set<string>,
  enabledMods?: Set<string>
): TagComplianceImageBox[] {
  if (!preview?.shelves) {
    return [];
  }

  const boxes: TagComplianceImageBox[] = [];

  for (const shelf of preview.shelves) {
    if (!isModEnabled(shelf, enabledMods)) {
      continue;
    }

    const outcome = shelfOutcome(shelf);
    const coords = shelf.templateBoxCoords ?? [];

    if (enabledLayers.has(outcome) && coords.length > 0) {
      const x1 = Math.min(...coords.map((c) => Math.min(c.x1, c.x2)));
      const y1 = Math.min(...coords.map((c) => Math.min(c.y1, c.y2)));
      const x2 = Math.max(...coords.map((c) => Math.max(c.x1, c.x2)));
      const y2 = Math.max(...coords.map((c) => Math.max(c.y1, c.y2)));

      const labelParts = [
        `M${shelf.mod}-S${shelf.shelf}`,
        `PT ${shelf.priceTags}/${shelf.pogPositions}`,
      ];
      if (shelf.skipReason) {
        labelParts.push(`skip: ${shelf.skipReason}`);
      } else if (shelf.subtaskType) {
        labelParts.push(
          `${shelf.subtaskType === 'missing_tags' ? 'missing' : 'extra'} ${shelf.count ?? ''}`
        );
      } else {
        labelParts.push('count OK → price check');
      }

      boxes.push({
        shape: [x1, y1, x2 - x1, y2 - y1],
        color: BBOX_COLOR_BY_LAYER.get(outcome) ?? '#9ca3af',
        strokeWidth: 8,
        opacity: 0,
        label: labelParts.join(' • '),
        tagComplianceItem: { kind: 'shelf', shelf },
      });
    }

    if (enabledLayers.has(TAG_ORPHAN_LAYER)) {
      for (const tag of shelf.orphanPriceTags ?? []) {
        const ocr = tag.ocr_text ? ` ${tag.ocr_text}` : '';
        boxes.push({
          shape: [
            Math.min(tag.x1, tag.x2),
            Math.min(tag.y1, tag.y2),
            Math.abs(tag.x2 - tag.x1),
            Math.abs(tag.y2 - tag.y1),
          ],
          color: ORPHAN_TAG_COLOR,
          strokeWidth: 6,
          label: `orphan${ocr}`,
          tagComplianceItem: { kind: 'orphan', shelf, tag },
        });
      }
    }
  }

  return boxes;
}

/**
 * How many boxes a layer would draw, under the same mod filter the overlay uses
 * — so the badge never disagrees with what is on the image.
 */
export function getTagComplianceLayerCount(
  preview: TagCompliancePreview | null | undefined,
  layerKey: string,
  enabledMods?: Set<string>
): number {
  const shelves = (preview?.shelves ?? []).filter((shelf) => isModEnabled(shelf, enabledMods));

  if (layerKey === TAG_ORPHAN_LAYER) {
    return shelves.reduce((sum, shelf) => sum + (shelf.orphanPriceTags ?? []).length, 0);
  }

  return shelves.filter(
    (shelf) => shelfOutcome(shelf) === layerKey && (shelf.templateBoxCoords ?? []).length > 0
  ).length;
}
